import { useSubmit } from "@certn/api";
import { BasicButton } from "@certn/cascadia";
import {
  Container,
  Grid,
  Layout,
  SecondaryTopNav,
} from "@certn/cascadia-react";
import { PrimaryButton, showToast, Text } from "@certn/cascadia";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string, number, boolean, mixed } from "yup";

import { AddressBasedTemplateFormFields } from "./AddressBasedTemplateFormFields";
import { CancelModal } from "./CancelModal";
import { MaximumPriceFields } from "./MaximumPriceFields";
import {
  ADDRESS_BASED_TEMPLATE_DEFAULTS,
  toMaximumPricePayload,
  type AddressBasedTemplateFormValues,
  type AddressBasedTemplatePageConfig,
} from "./types";

interface AddressBasedTemplateCreatePageProps {
  config: AddressBasedTemplatePageConfig;
}

export function AddressBasedTemplateCreatePage({
  config,
}: AddressBasedTemplateCreatePageProps) {
  const {
    apiPathPrefix,
    settingsPathPrefix,
    templateLabel,
    defaultLookbackYears = ADDRESS_BASED_TEMPLATE_DEFAULTS.DEFAULT_LOOKBACK_YEARS,
    showMaximumPrice = false,
  } = config;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const initialValues: AddressBasedTemplateFormValues = {
    name: "",
    minimum_duration_months:
      ADDRESS_BASED_TEMPLATE_DEFAULTS.DEFAULT_DURATION_MONTHS,
    lookback_years: defaultLookbackYears,
    apply_residency_rules_to_current_address: true,
    ...(showMaximumPrice
      ? { enable_maximum_price: false, maximum_price: {} }
      : {}),
  };

  const schema = object().shape({
    name: string().required().label(t("Template name")),
    minimum_duration_months: number()
      .required()
      .min(ADDRESS_BASED_TEMPLATE_DEFAULTS.MIN_DURATION_MONTHS)
      .max(ADDRESS_BASED_TEMPLATE_DEFAULTS.MAX_DURATION_MONTHS)
      .label(t("Minimum length of stay")),
    lookback_years: number()
      .required()
      .min(ADDRESS_BASED_TEMPLATE_DEFAULTS.MIN_LOOKBACK_YEARS)
      .max(ADDRESS_BASED_TEMPLATE_DEFAULTS.MAX_LOOKBACK_YEARS)
      .label(t("Look back period years")),
    apply_residency_rules_to_current_address: boolean().label(
      t("Apply residency rules to current address")
    ),
    ...(showMaximumPrice
      ? {
          maximum_price: mixed().when("enable_maximum_price", {
            is: true,
            then: (maximumPrice) =>
              maximumPrice.test(
                "has-positive-amount",
                t("Enter a price threshold of at least 1"),
                (value) =>
                  Object.values(
                    (value as AddressBasedTemplateFormValues["maximum_price"]) ??
                      {}
                  ).some((amount) => (amount ?? 0) >= 1)
              ),
          }),
        }
      : {}),
  });

  const listPath = `/settings/${settingsPathPrefix}`;

  const { submit } = useSubmit(
    `/api/internal/client-portal/check-arguments-templates/${apiPathPrefix}/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(t("Template successfully created"), { type: "success" });
        navigate(listPath);
      },
    }
  );

  const handleCancel = () => {
    setShowCancelModal(false);
    navigate(listPath);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await submit({
            name: values.name,
            check_arguments: {
              umbrella_minimum_address_duration_months:
                values.minimum_duration_months,
              umbrella_address_history_range_months: values.lookback_years * 12,
              umbrella_apply_residency_rules_to_current_address:
                values.apply_residency_rules_to_current_address,
              ...(showMaximumPrice
                ? { maximum_price: toMaximumPricePayload(values) }
                : {}),
            },
          });
        } catch (error) {
          console.error(error);
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Layout
          layout="narrow"
          header={
            <SecondaryTopNav
              testID="close-button"
              leftIcon="close"
              onleftIconOnPress={() => setShowCancelModal(true)}
            >
              <BasicButton
                onPress={() => setShowCancelModal(true)}
                isLoading={isSubmitting}
                testID="cancel-button"
              >
                {t("Cancel")}
              </BasicButton>
              <PrimaryButton
                onPress={() => handleSubmit()}
                isLoading={isSubmitting}
              >
                {t("Save template")}
              </PrimaryButton>
            </SecondaryTopNav>
          }
        >
          <Container>
            <Text as="h1" style="global/title-medium">
              {t("Create {{label}} template", {
                label: templateLabel(t).toLowerCase(),
              })}
            </Text>

            <Grid form>
              <AddressBasedTemplateFormFields
                isDisabled={false}
                showNameField={true}
                defaultLookbackYears={defaultLookbackYears}
              />
              {showMaximumPrice && <MaximumPriceFields isDisabled={false} />}
            </Grid>
            {showCancelModal && (
              <CancelModal
                onConfirm={handleCancel}
                onClose={() => setShowCancelModal(false)}
                isOpen={showCancelModal}
              />
            )}
          </Container>
        </Layout>
      )}
    </Formik>
  );
}
